import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Fetcher } from 'react-router';
import { useFetchers, useSearchParams } from 'react-router';
import type { PAGE_POSITIONS } from 'types';
import type { Image } from 'types/capi/shared';
import type { User } from 'types/User';

import BellFilled from '~/assets/icons/BellFilled';
import FlameIcon from '~/assets/icons/Flame';
import InformationIcon from '~/assets/icons/Information';
import NotifyMeBell from '~/assets/icons/NotifyMeBell';
import type { StockStatus } from '~/domain/stockStatus';
import ClickNotificationEvent from '~/events/ClickNotificationEvent';
import useCookiesAccepted from '~/hooks/useCookiesAccepted';
import useIsPendingConfirmation from '~/hooks/useIsPendingConfirmation';
import useRegion from '~/hooks/useRegion';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import { getPendingRegistrationEmail } from '~/utils/browser';
import { formatNumberToK } from '~/utils/numbers';

import Modals from './Modals';
import ConfirmEmailReminder from './Modals/Login/NotificationFlow/ConfirmEmailReminder';
import Tracked from './Tracked';

enum FetchState {
  Idle,
  Setting,
  Unsetting
}

function getFetchState(product: NotificationData, fetchers: Fetcher[]) {
  const url = `/bolt/notification/${product.id}`;
  const fetcher = fetchers.find((f) => f.formAction?.startsWith(url));

  if (!fetcher) return FetchState.Idle;

  return fetcher?.formData?.get('delete')
    ? FetchState.Unsetting
    : FetchState.Setting;
}

function getIsNotified(
  state: FetchState,
  product: NotificationData,
  user: User
) {
  if (state === FetchState.Unsetting) return false;
  if (state === FetchState.Setting) return user.isLoggedIn;

  return Boolean(
    user.actions?.alerts?.products?.some((p) => product.id === Number(p.id))
  );
}

export type NotificationData = {
  affiliateName?: string;
  id: number;
  image?: Image;
  legacyId: string;
  name: string;
  price: number | null;
  queryID?: string;
  stockStatus?: StockStatus;
  weeklyNotificationsCount?: number;
};

type MainProps = {
  buttonStyle: 'compact' | 'normal' | 'verbose';
  className?: string;
  mode?: 'light' | 'dark';
  openModalViaQueryString?: boolean;
  pagePosition: PAGE_POSITIONS | `TEMP_${string}`;
  product: NotificationData;
  showTooltip?: boolean;
  sticky?: boolean;
  variant?: 'none';
};

export default function ProductNotificationButton({
  product,
  buttonStyle,
  className,
  pagePosition,
  mode = 'light',
  sticky,
  showTooltip = false,
  openModalViaQueryString = false
}: MainProps) {
  const user = useUser();
  const fetchers = useFetchers();
  const [searchParams, setSearchParams] = useSearchParams();
  const cookiesAccepted = useCookiesAccepted();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmEmailModalOpen, setIsConfirmEmailModalOpen] = useState(false);

  useEffect(() => {
    const shouldOpenFromQueryString =
      openModalViaQueryString &&
      cookiesAccepted &&
      (searchParams.has('modal') ||
        searchParams.get('notify') === product.id.toString());

    if (shouldOpenFromQueryString) {
      setIsModalOpen(true);
    }
  }, [openModalViaQueryString, cookiesAccepted, searchParams, product.id]);

  const fetchState = getFetchState(product, fetchers);
  const isNotified = getIsNotified(fetchState, product, user);
  const { isBot, clientOs } = useRootData();

  const isPendingConfirmation = useIsPendingConfirmation(
    product.id,
    isNotified
  );

  const region = useRegion();

  function onClick(e: React.SyntheticEvent) {
    e.preventDefault();
    e.stopPropagation();

    setIsModalOpen(true);

    return new ClickNotificationEvent({
      buttonName: 'OPEN_NOTIFICATION',
      isBot,
      legacyProductId: product.legacyId,
      linkType: sticky ? 'sticky' : undefined,
      os: clientOs,
      pagePosition: pagePosition,
      productId: product.id,
      productName: product.name,
      queryID: product.queryID ?? localStorage.getItem('queryID'),
      user
    }).send();
  }

  function onPendingClick(e: React.SyntheticEvent) {
    e.preventDefault();
    e.stopPropagation();

    setIsConfirmEmailModalOpen(true);
  }

  function onModalDismiss() {
    setIsModalOpen(false);

    if (searchParams.has('modal') || searchParams.has('notify')) {
      setSearchParams((params) => {
        params.delete('modal');
        params.delete('notify');

        return params;
      });
    }
  }

  if (region.code !== 'GB') return null;

  function renderButton() {
    if (isPendingConfirmation) {
      if (buttonStyle === 'compact')
        return (
          <CompactPendingButton
            onClick={onPendingClick}
            pagePosition={pagePosition}
          />
        );
      if (buttonStyle === 'verbose')
        return (
          <VerbosePendingButton
            className={className}
            onClick={onPendingClick}
            pagePosition={pagePosition}
          />
        );

      return (
        <NormalPendingButton
          className={className}
          showTooltip={showTooltip}
          onClick={onPendingClick}
          pagePosition={pagePosition}
        />
      );
    }

    if (buttonStyle === 'compact') {
      return (
        <CompactButton
          isNotified={isNotified}
          mode={mode}
          isDisabled={fetchState !== FetchState.Idle}
          onClick={onClick}
          className={className}
          pagePosition={pagePosition}
        />
      );
    }

    return (
      <NormalButton
        isNotified={isNotified}
        showTooltip={showTooltip}
        onClick={onClick}
        mode={mode}
        isDisabled={fetchState !== FetchState.Idle}
        product={product}
        className={className}
        pagePosition={pagePosition}
      />
    );
  }

  return (
    <div className={className}>
      {renderButton()}
      <Modals.ProductNotification
        product={product}
        isOpen={isModalOpen}
        onDismiss={onModalDismiss}
      />
      {isConfirmEmailModalOpen && (
        <ConfirmEmailReminder
          email={getPendingRegistrationEmail() ?? ''}
          image={product.image}
          pagePosition="Confirm Modal"
          onDismiss={() => setIsConfirmEmailModalOpen(false)}
        />
      )}
    </div>
  );
}

type PendingButtonProps = {
  className?: string;
  onClick: (e: React.SyntheticEvent) => void;
  pagePosition: MainProps['pagePosition'];
  showTooltip?: boolean;
};

function NormalPendingButton({
  className = '',
  showTooltip = false,
  onClick,
  pagePosition
}: PendingButtonProps) {
  return (
    <div className="flex w-full overflow-hidden">
      {showTooltip && <PendingTooltip />}
      <Tracked.Button
        variant="none"
        onClick={onClick}
        name="CONFIRM_EMAIL_STATUS"
        pagePosition={pagePosition}
        data-cy="product-notification-button-pending"
        className={`z-20 flex w-full items-center justify-center gap-2 text-nowrap rounded-lg bg-golden-syrup px-4 py-2 ${className}`}
      >
        <InformationIcon width={16} height={16} />
        <span className="flex items-center font-semibold tracking-wide text-black">
          Confirm email
        </span>
      </Tracked.Button>
    </div>
  );
}

function CompactPendingButton({
  onClick,
  pagePosition
}: Pick<PendingButtonProps, 'onClick' | 'pagePosition'>) {
  return (
    <Tracked.Button
      variant="none"
      onClick={onClick}
      name="CONFIRM_EMAIL_STATUS"
      pagePosition={pagePosition}
      data-cy="product-notification-button-pending-compact"
      className="transition-transform duration-200 ease-in-out hover:scale-125"
    >
      <BellFilled fill="#FFAA00" width={18} height={18} />
    </Tracked.Button>
  );
}

function VerbosePendingButton({
  className = '',
  onClick,
  pagePosition
}: Pick<PendingButtonProps, 'className' | 'onClick' | 'pagePosition'>) {
  return (
    <Tracked.Button
      variant="none"
      onClick={onClick}
      name="CONFIRM_EMAIL_STATUS"
      pagePosition={pagePosition}
      data-cy="product-notification-button-pending"
      className={`flex w-full items-center justify-center gap-2 rounded-lg bg-golden-syrup p-2 ${className}`}
    >
      <InformationIcon width={16} height={16} />
      <span className="hidden items-center font-semibold tracking-wide text-black md:flex">
        Confirm email
      </span>
      <span className="flex items-center font-semibold leading-tight tracking-wide text-black md:hidden">
        Confirm your email to receive notifications
      </span>
    </Tracked.Button>
  );
}

type ButtonProps = {
  className?: string;
  isDisabled: boolean;
  isNotified: boolean;
  mode: 'light' | 'dark';
  pagePosition: MainProps['pagePosition'];
  showTooltip?: boolean;
} & React.HTMLProps<HTMLButtonElement>;

const CompactButton = ({
  onClick,
  mode,
  pagePosition,
  isDisabled,
  isNotified
}: ButtonProps) => {
  return (
    <Tracked.Button
      className="transition-transform duration-200 ease-in-out hover:scale-125"
      onClick={onClick}
      disabled={isDisabled}
      type="submit"
      data-cy="product-notification-button-compact"
      name="OPEN_NOTIFICATION"
      pagePosition={pagePosition}
      variant="none"
    >
      <NotifyMeBell
        mode={mode}
        variant={isNotified ? 'filled' : 'outline-thin'}
        height={20}
        width={20}
      />
    </Tracked.Button>
  );
};

const NormalButton = ({
  product,
  isNotified,
  showTooltip,
  mode,
  isDisabled,
  onClick,
  pagePosition,
  className
}: ButtonProps & { product: NotificationData }) => {
  return (
    <Tracked.Button
      type="submit"
      className="flex w-full overflow-hidden"
      onClick={onClick}
      disabled={isDisabled}
      data-cy="product-notification-button-normal"
      name="OPEN_NOTIFICATION"
      pagePosition={pagePosition}
      variant="none"
    >
      {showTooltip && !isNotified && <Tooltip product={product} />}
      <div className={getNormalButtonStyles(isNotified, mode, className)}>
        <NotifyMeBell
          mode={mode}
          variant={isNotified ? 'filled' : 'outline-thick'}
        />
        <span className="flex items-center font-heading font-medium tracking-wide">
          {isNotified ? 'Notified' : 'Notify me'}
        </span>
      </div>
    </Tracked.Button>
  );
};

const PendingTooltip = () => {
  return (
    <div className="z-10 -mr-2 flex animate-slide-from-right items-center self-stretch whitespace-nowrap bg-golden-syrup-light pl-2 pr-4 text-black opacity-0">
      Set up incomplete
    </div>
  );
};

const Tooltip = ({ product }: { product: NotificationData }) => {
  const { t } = useTranslation();

  const { stockStatus, weeklyNotificationsCount = 0 } = product;

  return (
    <div className="z-10 -mr-2 flex animate-slide-from-right items-center self-stretch whitespace-nowrap bg-purple-hola/5 pl-2 pr-4 text-purple-hola opacity-0">
      {weeklyNotificationsCount >= 50 ? (
        <div className="flex gap-1 whitespace-nowrap">
          <FlameIcon width={16} height={16} />
          {formatNumberToK(weeklyNotificationsCount)}
          <span className="hidden sm:block">notifications</span> set in the last
          7 days
        </div>
      ) : (
        <>
          {['in-stock', 'reseller'].includes(stockStatus!) &&
            t('getPriceDropAlert')}
          {['coming-soon', 'tbc'].includes(stockStatus!) &&
            t('getReleaseAlert')}
          {stockStatus === 'sold-out' && 'Get a restock alert'}
        </>
      )}
    </div>
  );
};

function getNormalButtonStyles(
  isNotified: boolean,
  mode: 'dark' | 'light',
  className: string = ''
) {
  return `flex w-full items-center justify-center gap-2 rounded-lg p-2 transition duration-200 ease-in-out z-20 ${className} ${
    isNotified
      ? `border  ${mode === 'dark' ? 'text-purple-hola-light border-purple-hola-light' : 'text-purple-hola border-purple-hola'}`
      : `${mode === 'dark' ? 'text-black bg-purple-hola-light' : 'text-white bg-purple-hola'}`
  }`;
}
